/**
 * API client.
 *
 * Everything the UI does goes through here, so the auth token, error shape and
 * transport are handled in exactly one place. When the backend moved from a
 * Netlify Function to a Google Apps Script web app, this file was the seam —
 * and nothing above it changed.
 *
 * ── Why every call is a POST with a text/plain body ────────────────────────
 *
 * The frontend is served from Netlify and the backend from script.google.com,
 * so every call is cross-origin. An Apps Script web app has no doOptions and
 * cannot set response headers, which means it can never answer a CORS
 * preflight. So the browser must never be given a reason to send one, and
 * three things that would each cause one are off the table:
 *
 *   - an `Authorization` header  -> the token travels in the body instead
 *   - `PATCH` / `DELETE`         -> the real method travels in the body
 *   - `Content-Type: application/json` -> sent as text/plain, parsed as JSON
 *
 * What survives is a "simple request", which the browser sends directly.
 * Moving the token out of a header costs nothing here: it was never a cookie,
 * so a hostile page could always call the endpoint, and still has no way to
 * make the browser attach a signed-in user's credentials.
 *
 * ── Why the response carries its own status ────────────────────────────────
 *
 * ContentService always answers HTTP 200. The real status is a field in the
 * body, which this file unwraps and rethrows as an ApiError — so callers see
 * exactly the status codes they saw when the API was a Netlify Function.
 */

const PLACEHOLDER = 'PASTE_YOUR_APPS_SCRIPT_URL_HERE';
const TOKEN_KEY = 'provia.token';

/**
 * Where the backend lives.
 *
 * Read at runtime from /config.js — a plain file sitting next to index.html in
 * the deployed folder — so changing the backend URL is a one-line edit rather
 * than a rebuild. That is what lets the built folder be uploaded to Netlify
 * as-is and pointed at your own Apps Script deployment afterwards.
 *
 * VITE_APPS_SCRIPT_URL still works as a fallback for git-based builds, where
 * baking it in at build time is the more natural thing.
 */
function endpointUrl(): string | null {
  const runtime = (window as any).PROVIA_CONFIG?.apiUrl;
  if (typeof runtime === 'string' && runtime && runtime !== PLACEHOLDER) {
    return runtime.trim().replace(/\/+$/, '');
  }
  const buildTime = import.meta.env.VITE_APPS_SCRIPT_URL;
  if (typeof buildTime === 'string' && buildTime && buildTime !== PLACEHOLDER) {
    return buildTime.trim().replace(/\/+$/, '');
  }
  return null;
}

/** True when config.js is still the shipped placeholder. */
export function isConfigured(): boolean {
  return endpointUrl() !== null;
}

export class ApiError extends Error {
  status: number;
  detail?: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private browsing with storage disabled — the session just won't persist.
  }
}

/** Fires when the server rejects our token, so the app can bounce to login. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};
export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Set false for login calls, where a 401 is a normal answer. */
  handleAuthFailure?: boolean;
}

/** The envelope the Apps Script router unwraps back into a Req. */
interface Envelope {
  path: string;
  method: string;
  token: string | null;
  query: Record<string, string>;
  body: unknown;
}

function endpoint(): string {
  const url = endpointUrl();
  if (!url) {
    throw new ApiError(
      0,
      'This site has not been connected to its backend yet. '
      + 'Edit config.js and paste in the backend URL.',
    );
  }
  return url;
}

export async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, handleAuthFailure = true } = options;

  const cleanQuery: Record<string, string> = {};
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') cleanQuery[k] = String(v);
    }
  }

  const envelope: Envelope = {
    path,
    method,
    token: getToken(),
    query: cleanQuery,
    body: body === undefined ? {} : body,
  };

  let res: Response;
  try {
    res = await fetch(endpoint(), {
      method: 'POST',
      // text/plain keeps this a "simple request". The router parses it as JSON.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(envelope),
      // Apps Script answers with a 302 to script.googleusercontent.com and the
      // real payload is behind it. This is the default, stated for clarity.
      redirect: 'follow',
    });
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, 'Could not reach the server. Check your connection.');
  }

  // A non-200 here is the platform failing, not the API: a bad deployment id,
  // or a web app that is not shared with "Anyone".
  if (!res.ok) {
    throw new ApiError(
      res.status,
      // Kept vendor-neutral: a user hitting a misconfigured deployment should
      // see a service message, not our deployment topology.
      res.status === 401 || res.status === 403
        ? 'The service is not reachable right now. Please try again shortly.'
        : `Could not reach the service (HTTP ${res.status}).`,
    );
  }

  let payload: { status: number; ok: boolean; data: any } | null = null;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError(502, 'The API returned a response that could not be read.');
  }
  if (!payload || typeof payload.status !== 'number') {
    throw new ApiError(502, 'The API returned an unexpected response.');
  }

  if (payload.status === 401 && handleAuthFailure) {
    setToken(null);
    onUnauthorized();
  }

  if (!payload.ok) {
    throw new ApiError(
      payload.status,
      payload.data?.error ?? `Request failed (${payload.status})`,
      payload.data?.detail,
    );
  }

  if (payload.status === 204) return undefined as T;
  return payload.data as T;
}

export const api = {
  get: <T = any>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  /*
   * Takes a query, which the others always could. Deleting is the one verb
   * where a flag ("yes, really, for good") belongs in the URL rather than in
   * a body — a DELETE body is legal but widely dropped in transit, and this
   * one travels inside the POST envelope where it would survive, which is
   * exactly the kind of detail that stops being true one transport later.
   */
  del: <T = any>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'DELETE', query }),
};

/** The envelope /files/:id/content returns instead of raw bytes. */
interface BinaryPayload {
  __binary: true;
  mime_type: string;
  filename: string;
  data: string;
}

/**
 * Fetches a stored file and hands back an object URL.
 *
 * Apps Script cannot return arbitrary bytes with a chosen Content-Type, so the
 * route answers with base64 in a JSON envelope and the Blob is rebuilt here.
 * That is no loss: the previous version already had to fetch and wrap, because
 * `<img src>` cannot send an auth header either.
 *
 * Callers must revoke the URL on unmount.
 */
export async function fetchFileBlobUrl(fileId: string): Promise<string> {
  const payload = await request<BinaryPayload>(`/files/${fileId}/content`);
  if (!payload || !payload.__binary) {
    throw new ApiError(502, 'Could not load that file');
  }
  return URL.createObjectURL(base64ToBlob(payload.data, payload.mime_type));
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}

/**
 * Reads a File into a base64 payload, downscaling images first.
 *
 * Apps Script accepts a much larger request body than a Netlify Function did
 * (~6 MB there), so the limit is no longer the transport — it is the 6-minute
 * execution budget the script has to decode the base64 and hand it to Drive.
 * A downscaled photo uploads in a second or two; a raw 12 MP one does not.
 */
export async function fileToUpload(
  file: File,
  opts: { maxDimension?: number; quality?: number } = {},
): Promise<{ filename: string; mime_type: string; data: string }> {
  const { maxDimension = 2000, quality = 0.82 } = opts;

  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return {
      filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      data: await readAsBase64(file),
    };
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));

    // Already small enough — don't re-encode and lose quality for nothing.
    if (scale === 1 && file.size < 1_500_000) {
      bitmap.close();
      return { filename: file.name, mime_type: file.type, data: await readAsBase64(file) };
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas context');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    return {
      filename: file.name.replace(/\.(png|webp|heic|heif)$/i, '.jpg'),
      mime_type: 'image/jpeg',
      data: dataUrl.slice(dataUrl.indexOf(',') + 1),
    };
  } catch {
    // Any failure in the resize path: send the original and let the server decide.
    return { filename: file.name, mime_type: file.type, data: await readAsBase64(file) };
  }
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });
}
